import type {
	Column,
	ColumnData,
} from '@/components/Organisms/DataTable/DataTable.types';
import { colors } from '@/components/ui/theme';
import { createdOnDateRanges } from '@/constants';
import type { PriorAuth } from '@/types/benefitsVerification';
import type { CommissionableProduct } from '@/types/orderReturns';
import type { ClinicProducts } from '@/types/product';

/**
 * BV Status code mapping with colors
 */
const BV_STATUS_MAP: Record<string, { label: string; color: string }> = {
  // Existing
  Approved: { label: 'Approved', color: colors.bvApproved },
  'Auth Approved': { label: 'Auth Approved', color: colors.bvAuthApproved },
  'Appeal In Process': {
    label: 'Appeal In Process',
    color: colors.bvAppealInProcess,
  },
  'Auth In Process': {
    label: 'Auth In Process',
    color: colors.bvAppealInProcess,
  },
  'In Process': { label: 'In Process', color: colors.bvInProcess },

  'Pending Clinic Response (Missing Data)': {
    label: 'Pending Clinic Response (Missing Data)',
    color: colors.bvPendingClinicResponse,
  },

  Void: { label: 'Void', color: colors.bvVoid },
  'Auth Denied': { label: 'Auth Denied', color: colors.bvAuthDenied },
  Denied: { label: 'Denied', color: colors.bvDenied },

  'Order Placed': {
    label: 'Order Placed',
    color: colors.bvOrderPlaced,
  },

  Mixed: { label: 'Mixed', color: colors.bvMixed },
  Incomplete: { label: 'Incomplete', color: colors.defaultGray },
  'Void BV': { label: 'Void', color: colors.bvVoid },

  // -------- Added Missing Statuses --------

  'Chart Review': {
    label: 'Chart Review',
    color: colors.bvInProcess,
  },

  'Chart Review Denied': {
    label: 'Chart Review Denied',
    color: colors.bvDenied,
  },

  'Results Recieved - Pending UL+Review': {
    label: 'Results Received - Pending UL Review',
    color: colors.bvInProcess,
  },

  Appealed: {
    label: 'Appealed',
    color: colors.bvAppealInProcess,
  },

  'Approved/Auth Started': {
    label: 'Approved/Auth Started',
    color: colors.bvApproved,
  },

  'Approved/No Action': {
    label: 'Approved/No Action',
    color: colors.bvApproved,
  },

  'Approved/Pending Records': {
    label: 'Approved/Pending Records',
    color: colors.bvPendingClinicResponse,
  },

  'Approved/Revised': {
    label: 'Approved/Revised',
    color: colors.bvApproved,
  },

  'Denied OON/Revised': {
    label: 'Denied OON/Revised',
    color: colors.bvDenied,
  },

  'Denied Policy/Revised': {
    label: 'Denied Policy/Revised',
    color: colors.bvDenied,
  },

  'Denied/No Action': {
    label: 'Denied/No Action',
    color: colors.bvDenied,
  },

  'Missing Data': {
    label: 'Missing Data',
    color: colors.bvPendingClinicResponse,
  },

  New: {
    label: 'New',
    color: colors.defaultGray,
  },

  'Partially Approved': {
    label: 'Partially Approved',
    color: colors.bvMixed,
  },

  Pending: {
    label: 'Pending',
    color: colors.bvInProcess,
  },

  'Portal Draft': {
    label: 'Portal Draft',
    color: colors.defaultGray,
  },
};

/**
 * Generic function to determine display text based on an array of items
 * - If length > 1: return multipleText (default: "Multiple Items")
 * - If length === 1: return the value extracted by getDisplayValue
 * - If length === 0: return emptyText (default: "-")
 *
 * @param items - Array of items of type T
 * @param getDisplayValue - Function to extract the display value from a single item
 * @param multipleText - Text to display when multiple items exist
 * @param emptyText - Text to display when no items exist
 */
export const getDisplayFromArray = <T>(
	items: T[] | null | undefined,
	getDisplayValue: (item: T) => string,
	multipleText = 'Multiple Items',
	emptyText = '-',
): string => {
	if (!items || items.length === 0) {
		return emptyText;
	}

	if (items.length > 1) {
		return multipleText;
	}

	return getDisplayValue(items[0]);
};

/**
 * Determines product display text based on priorAuths array
 * - If length > 1: return "Multiple Products"
 * - If length === 1: return the ta_name
 * - If length === 0: return "-"
 */
export const getProductDisplay = (priorAuths: PriorAuth[]): string => {
	return getDisplayFromArray(
		priorAuths,
		(item) => item.ta_name,
		'Multiple Products',
		'-',
	);
};

/**
 * Maps BV status code to status label
 * @param statusCode - The ta_bv_status code from API
 * @returns The status label string
 */
export const getBVStatusLabel = (statusCode: number): string => {
	return BV_STATUS_MAP[statusCode]?.label || '-';
};

/**
 * Maps BV status code to status object with label and color
 * @param statusCode - The ta_bv_status code from API
 * @returns Object with label and color, or default values
 */
export const getBVStatus = (
	statusCode: string | null | undefined,
): { label: string; color: string } => {
	// Handle null or undefined status as "Incomplete"
	if (statusCode === null || statusCode === undefined || statusCode === '') {
		return { label: 'Incomplete', color: colors.defaultGray };
	}
	return BV_STATUS_MAP[statusCode] || { label: '-', color: colors.bvVoid };
};

// Status filter options matching the image
export const statusesFilterOptions = [
	{ label: 'In Process', value: 'In Process' },
	{ label: 'Approved', value: 'Approved' },
	{ label: 'Auth Approved', value: 'Auth Approved' },
	{ label: 'Order Placed', value: 'Order Placed' },
	// { label: 'Appeal in Process', value: 'Appeal In Process' },
	{
		label: 'Pending Clinic Response (Missing Data)',
		value: 'Pending Clinic Response (Missing Data)',
	},
	{ label: 'Denied', value: 'Denied' },
	// { label: 'Auth Denied', value: 'Auth Denied' },
	{ label: 'Void', value: 'Void' },
	{ label: 'Incomplete', value: 'Incomplete' },
	// { label: 'On Hold', value: 'on-hold' },
];

export const approvedDeniedMapping = {
	'Denied': ['Denied', 'Auth Denied'],
	'Approved': ['Approved', 'Auth Approved'],
	'Auth Approved': ['Approved', 'Auth Approved'], 
	'Auth Denied': ['Denied', 'Auth Denied'],
};

export const statusesFilterOptionsColumn = [
	{ label: 'In Process', value: 'In Process' },
	{ label: 'Approved', value: 'Approved' },
	{ label: 'Auth Approved', value: 'Auth Approved' },
	{ label: 'Order Placed', value: 'Order Placed' },
	{ label: 'Appeal in Process', value: 'Appeal In Process' },
	{
		label: 'Pending Clinic Response (Missing Data)',
		value: 'Pending Clinic Response (Missing Data)',
	},
	{ label: 'Denied', value: 'Denied' },
	{ label: 'Auth Denied', value: 'Auth Denied' },
	{ label: 'Void', value: 'Void' },
	{ label: 'Incomplete', value: 'Incomplete' },
];

// Columns matching the image
export const columns: Column[] = [
	{
		key: 'account.name',
		label: 'Clinic',
		width: '180px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'provider.fullname',
		label: 'Provider',
		width: '150px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'patient.fullname',
		label: 'Patient',
		width: '150px',
		type: 'string',
		sorter: true,
	},
	{
		key: 'ta_proceduredate',
		label: 'Procedure Date',
		width: '120px',
		type: 'date',
		sorter: true,
	},
	{
		key: 'priorAuths.ta_name',
		label: 'Product',
		width: '200px',
		type: 'string',
	},
	{
		key: 'createdon',
		label: 'Created On',
		width: '150px',
		type: 'date',
		customDateRanges: createdOnDateRanges,
		sorter: true,
	},
	{
		key: 'bv_product_status',
		label: 'Status',
		width: '150px',
		type: 'string-status',
		filterOptions: statusesFilterOptionsColumn,
	},
];

export const getBvStatuses = () => {
	// if (['develop', 'stage'].includes(import.meta.env.VITE_ENV)) {
	// 	return productStatuesQA;
	// } else if (['prod', 'preprod'].includes(import.meta.env.VITE_ENV)) {
	// 	return productStatuesProd;
	// }
	return productStatuesProd;
};

export const bvStatusMap: Record<number, string> = {
  953870003: "Chart Review",
  953870006: "Chart Review Denied",
  953870020: "Results Recieved - Pending UL+Review",
  953870017: "Appealed",
  953870002: "Approved",
  953870014: "Approved/Auth Started",
  953870009: "Approved/No Action",
  953870015: "Approved/Pending Records",
  953870010: "Approved/Revised",
  953870018: "Auth Approved",
  953870019: "Auth Denied",
  953870005: "Denied",
  953870012: "Denied OON/Revised",
  953870013: "Denied Policy/Revised",
  953870011: "Denied/No Action",
  953870008: "In Process",
  953870016: "Missing Data",
  953870000: "New",
  953870025: "Order Placed",
  953870001: "Partially Approved",
  953870004: "Pending",
  953870007: "Portal Draft",
  953870026: "Void BV"
};

export const productStatuesQA = {
	'953870003': 'In Process',
	'953870001': 'Approved',
	'953870002': 'Denied',
	'953870004': 'Auth In Process',
	'953870005': 'Auth Denied',
	'953870006': 'Auth Approved',
	'953870007': 'Appeal In Process',
	'953870000': 'Pending Clinic Response (Missing Data)',
};
export const productStatuesProd = {
	'953870003': 'In Process',
	'953870001': 'Approved',
	'953870002': 'Denied',
	'953870000': 'Auth In Process',
	'953870008': 'In Process',
	'953870004': 'Auth Denied',
	'953870005': 'Auth Approved',
	'953870006': 'Appeal In Process',
	'953870007': 'Pending Clinic Response (Missing Data)',
};

export const STATUS_ACTIONS_MAP: Record<string, string[]> = {
	// In Process statuses
	'In Process': ['Edit', 'Details'],
	'Auth In Process': ['Edit', 'Details'],
	'Appeal In Process': ['Edit', 'Details'],
	'Pending Clinic Response (Missing Data)': ['Edit', 'Details'],

	// Incomplete status
	Incomplete: ['Edit', 'Details'],

	// Approved status
	Approved: [
		'Details',
		'View IVR',
		'Void Now',
		'Order Now', // Only if no order placed for this BV
		'View PR Document', // Only if Medicare Part B
	],

	// Auth Approved status
	'Auth Approved': [
		'Details',
		'View IVR',
		'Order Now',
		'Void Now',
		'View PR Document', // Only if Medicare Part B
	],

	// Denied statuses
	Denied: ['Details', 'View IVR'],
	'Auth Denied': ['Details', 'View IVR'],

	// Void status
	Void: ['Details', 'View IVR'],

	// Order Placed status
	'Order Placed': [
		'Details',
		'View IVR',
		'Reorder',
		'View PR Document', // Only if Medicare Part B
	],

	// Mixed status (for multiple products with different statuses)
	Mixed: ['Details'],
};
export const STATUS_SUB_ACTIONS_MAP: Record<string, string[]> = {
	Approved: ['View IVR', 'Order Now'],
	'Auth Approved': ['View IVR', 'Order Now'],

	Denied: ['View IVR'],
	'Auth Denied': ['View IVR'],
	Void: ['View IVR'],

	'Order Placed': ['View IVR', 'Reorder'],
};

export const getQCodeFromProductNameAndQCode = (value: string = ''): string => {
	if (value.indexOf('(') >= 0 && value.lastIndexOf(')') >= 0) {
		// if the (Qcode) format is in the name. will return the Qcode
		return value.slice(value.indexOf('(') + 1, value.lastIndexOf(')'));
	} else {
		// if isnt in the name. will return the value as is it
		return value;
	}
};

export const getNameFromProductNameAndQCode = (value: string = ''): string => {
	if (value.indexOf('(') >= 0) {
		// if the (Qcode) format is in the name. will return the name without the Qcode
		return value.slice(0, value.indexOf('(')).trim();
	} else {
		// if isnt in the name. will return the value as is it
		return value;
	}
};

export const shouldShowAction = ({
	item,
	actionLabel,
	isSubRow = false,
	commissionableProducts = [],
	clinicProducts = {},
}: {
	item: ColumnData;
	actionLabel: string;
	isSubRow?: boolean;
	commissionableProducts?: CommissionableProduct[];
	clinicProducts?: ClinicProducts;
}): boolean => {
	const bvStatuses = typeof item?.bv_product_status === 'string' ? (item.bv_product_status as string).split(',') : [item.status as string];
	// Get the last value from the bvStatus array, which represents the overall status for main rows with multiple products. For sub-rows or single products, this will just be the status of that product.
	const bvStatus = bvStatuses[bvStatuses.length - 1] || '';

	// This contains any Product that is already ordered from the BV
	const selectedPriorAuth = item._ta_selectedpriorauth_value;
	const bvAuth = item.auth as unknown as PriorAuth;

	const isSingleProduct = !isSubRow && (!item.subRows || item.subRows.length === 0);
	const clinicKey =
		(typeof item.clinic === 'object' &&
			item.clinic !== null &&
			'value' in item.clinic
			? String((item.clinic as { value: unknown }).value)
			: String(item.clinic ?? '')) ||
		String((item.account as { accountid?: unknown } | undefined)?.accountid ?? '');
	const availableClinicProducts = clinicProducts?.[clinicKey] || [];
	
	// For sub-rows or single products, check the actual status of THIS product
	// For main rows with multiple products (Mixed status), check bvProductStatus
	const statusToCheck = (isSubRow || isSingleProduct) ? item.status : item.bvProductStatus;
	if ((statusToCheck as string)?.includes('Order Placed')) {
		return false;
	}

	const status = (bvStatus || 'Mixed') as string;
	const allowedActions =
		(isSubRow ? STATUS_SUB_ACTIONS_MAP[status] : STATUS_ACTIONS_MAP[status]) ||
		[];
	if (!allowedActions.includes(actionLabel)) {
		return false;
	}
	const isMedicarePartB = [
		(item['patient.primaryInsurance.ta_name'] as string)?.toLowerCase(),
		(item['patient.secondaryInsurance.ta_name'] as string)?.toLowerCase(),
	].includes('medicare (part b)');
	
	const productQCode = getQCodeFromProductNameAndQCode(
		(item.auth as unknown as PriorAuth)?.ta_name as string,
	);
	const isProductOrderable =
	// If there's a selected prior auth, the product must match that prior auth. If there's no selected prior auth, we allow it to be orderable as long as it meets the other criteria.
	(!selectedPriorAuth || bvAuth.ta_priorauthid === selectedPriorAuth) &&
		// Check if the product is commissionable and available in the clinic products
		commissionableProducts.some((product) => product.ta_qcode === productQCode) &&
		// Check if the product is available in the clinic products
		availableClinicProducts.some((product) =>
			typeof product === 'string'
				? product === productQCode
				: (product as { qcode?: string; ta_qcode?: string }).qcode === productQCode ||
				  (product as { qcode?: string; ta_qcode?: string }).ta_qcode === productQCode,
		);

	if (actionLabel === 'View PR Document' && !isMedicarePartB) {
		return false;
	}

	if (
		['Order Now', 'Reorder'].includes(actionLabel) &&
		!isProductOrderable
	) {
		return false;
	}

	// Block View IVR for: In Process, Auth In Process, Appeal In Process, Pending Clinic Response
	// Allow View IVR for: Approved, Auth Approved, Denied, Auth Denied, Void, Order Placed
	if (
		['View IVR'].includes(actionLabel) &&
		[953870008, 953870003, 953870000, 953870006, 953870007].includes((item.auth as unknown as PriorAuth)?.ta_pa_status)
	) {
		return false;
	}

	if (
		['View PR Document', 'View IVR', 'Order Now', 'Reorder'].includes(
			actionLabel,
		) &&
		!isSubRow &&
		item.subRows?.length &&
		item.subRows?.length > 0
	) {
		return false;
	}
	
	return true;
};
